import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// Material-UI
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import Chip from '@material-ui/core/Chip';
// Icons
import { X, User } from 'tabler-icons-react';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
// Context
import { useUsuarios } from './UsuariosContext';
import { useGeneral } from '../../Context/GeneralContext';

export default function ModalEditarUsuario({ open, onClose }) {
    const { theUser, roles, setRoles, ordenaArrObj, gpos, deptos, languages, editUser } =
        useUsuarios();
    const { tokenR } = useGeneral();

    const [phase, setPhase] = useState(1);
    const [form, setForm] = useState({
        nombre: '',
        apellido: '',
        email: '',
        phoneNumber: '',
        notas: '',
    });
    const [gpo, setGpo] = useState('');
    const [depto, setDepto] = useState('');
    const [language, setLanguage] = useState('');
    const [levels, setLevels] = useState({ one: false, two: false, three: false });
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Precarga datos del usuario — sin mutar el array de roles directamente
    useEffect(() => {
        if (!theUser || !open) return;
        setForm({
            nombre: theUser.nombre || '',
            apellido: theUser.apellido || '',
            email: theUser.email || '',
            phoneNumber: theUser.phoneNumber || '',
            notas: theUser.notas || '',
        });
        setGpo(theUser.grupo ?? '');
        setDepto(theUser.departamento ?? '');
        setLanguage(theUser.idioma ?? '');
        setLevels({ one: !!theUser.esNivel1, two: !!theUser.esNivel2, three: !!theUser.esNivel3 });

        // Marcar roles del usuario sin mutar — crear nuevo array con colores actualizados
        if (theUser.roles) {
            const userRoleSet = new Set(theUser.roles);
            const updatedRoles = roles.map((rol) => ({
                ...rol,
                color: userRoleSet.has(rol.nombre) ? 'primary' : 'default',
            }));
            updatedRoles.sort(ordenaArrObj);
            setRoles(updatedRoles);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theUser, open]);

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const toggleRole = (name) => {
        const newRoles = roles.map((rol) =>
            rol.nombre === name
                ? { ...rol, color: rol.color === 'primary' ? 'default' : 'primary' }
                : rol
        );
        newRoles.sort(ordenaArrObj);
        setRoles(newRoles);
    };

    const handleClose = () => {
        setPhase(1);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError(false);
        setShowPassword(false);
        onClose();
    };

    const saveUser = () => {
        editUser({
            usuario: theUser.usuario,
            password: 'null',
            email: form.email,
            phoneNumber: form.phoneNumber,
            id: null,
            nombre: form.nombre,
            apellido: form.apellido,
            cardId: null,
            notas: form.notas,
            departamento: depto,
            grupo: gpo,
            esNivel1: levels.one,
            esNivel2: levels.two,
            esNivel3: levels.three,
            idioma: language,
            horario: null,
            roles: roles.filter((r) => r.color === 'primary').map((r) => r.nombre),
        });
        handleClose();
    };

    const confirmNewPassword = () => {
        if (!newPassword.trim()) {
            setPasswordError(true);
            return;
        }
        setPasswordError(false);
        setPhase(3);
    };

    const savePassword = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/usuarios/restaurarcontrasena`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        authorization: `Bearer ${tokenR.current}`,
                    },
                    body: JSON.stringify({ usuario: theUser.usuario, password: newPassword }),
                }
            );
            if (!res.ok) throw new Error('Error al cambiar contraseña');
            setNewPassword('');
            setConfirmPassword('');
            setPhase(4);
        } catch (err) {
            console.error(err);
        }
    };

    const titles = {
        1: 'Editar usuario',
        2: 'Cambiar contraseña',
        3: 'Confirmar cambio',
        4: 'Contraseña actualizada',
    };

    const renderBody = () => {
        switch (phase) {
            case 1:
                return (
                    <>
                        <div className="usuarios-modal-body">
                            {/* ── Información ── */}
                            <div className="usuarios-form-section">
                                <p className="usuarios-form-section-title">Información personal</p>
                                <div className="usuarios-form-grid">
                                    <TextField
                                        label="Nombre de usuario"
                                        value={theUser?.usuario || ''}
                                        disabled
                                        fullWidth
                                        className="usuarios-form-full"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <User size={18} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label="Nombre"
                                        value={form.nombre}
                                        onChange={set('nombre')}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Apellidos"
                                        value={form.apellido}
                                        onChange={set('apellido')}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Correo"
                                        type="email"
                                        value={form.email}
                                        onChange={set('email')}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Teléfono"
                                        value={form.phoneNumber}
                                        onChange={set('phoneNumber')}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Notas"
                                        value={form.notas}
                                        onChange={set('notas')}
                                        fullWidth
                                        className="usuarios-form-full"
                                    />
                                </div>
                            </div>

                            <hr className="usuarios-form-divider" />

                            {/* ── Configuración ── */}
                            <div className="usuarios-form-section">
                                <p className="usuarios-form-section-title">Configuración</p>
                                <div className="usuarios-form-grid">
                                    <div>
                                        <InputLabel>Grupo</InputLabel>
                                        <Select
                                            fullWidth
                                            value={gpo ?? ''}
                                            onChange={(e) => setGpo(e.target.value)}
                                        >
                                            {gpos.map((i) => (
                                                <MenuItem key={i.id} value={i.id}>
                                                    {i.nombre}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                    <div>
                                        <InputLabel>Departamento</InputLabel>
                                        <Select
                                            fullWidth
                                            value={depto ?? ''}
                                            onChange={(e) => setDepto(e.target.value)}
                                        >
                                            {deptos.map((i) => (
                                                <MenuItem key={i.id} value={i.id}>
                                                    {i.nombre}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                    <div>
                                        <InputLabel>Idioma</InputLabel>
                                        <Select
                                            fullWidth
                                            value={language ?? ''}
                                            onChange={(e) => setLanguage(e.target.value)}
                                        >
                                            {languages.map((i) => (
                                                <MenuItem key={i.id} value={i.id}>
                                                    {i.nombre}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">Niveles</FormLabel>
                                        <FormGroup row>
                                            {[
                                                ['one', 1],
                                                ['two', 2],
                                                ['three', 3],
                                            ].map(([key, n]) => (
                                                <FormControlLabel
                                                    key={key}
                                                    control={
                                                        <Checkbox
                                                            checked={levels[key] || false}
                                                            onChange={(e) =>
                                                                setLevels((l) => ({
                                                                    ...l,
                                                                    [key]: e.target.checked,
                                                                }))
                                                            }
                                                        />
                                                    }
                                                    label={`Nivel ${n}`}
                                                />
                                            ))}
                                        </FormGroup>
                                        <FormHelperText>Puede marcar más de uno</FormHelperText>
                                    </FormControl>
                                </div>
                            </div>

                            <hr className="usuarios-form-divider" />

                            {/* ── Cargos ── */}
                            <div className="usuarios-form-section">
                                <p className="usuarios-form-section-title">Roles del usuario</p>
                                <div className="usuarios-roles-chips">
                                    {roles.map((rol) => (
                                        <Chip
                                            key={rol.nombre}
                                            label={rol.label ?? rol.nombre}
                                            clickable
                                            onClick={() => toggleRole(rol.nombre)}
                                            color={rol.color}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="usuarios-modal-footer">
                            <Button
                                variant="outlined"
                                color="secondary"
                                className="usuarios-modal-secondary"
                                onClick={() => setPhase(2)}
                            >
                                Cambiar contraseña
                            </Button>
                            <Button
                                variant="outlined"
                                className="usuarios-modal-cancel"
                                onClick={handleClose}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                className="usuarios-modal-submit"
                                onClick={saveUser}
                            >
                                Guardar
                            </Button>
                        </div>
                    </>
                );

            case 2:
                return (
                    <>
                        <div className="usuarios-modal-body">
                            <div className="usuarios-form-section">
                                <p className="usuarios-form-section-title">Nueva contraseña</p>
                                <TextField
                                    label="Contraseña"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        if (passwordError && e.target.value.trim())
                                            setPasswordError(false);
                                    }}
                                    fullWidth
                                    error={passwordError}
                                    helperText={passwordError ? 'Debe completar el campo' : ''}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setShowPassword((p) => !p)}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label="Confirmar contraseña"
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    fullWidth
                                    error={
                                        confirmPassword.length > 0 &&
                                        confirmPassword !== newPassword
                                    }
                                    helperText={
                                        confirmPassword.length > 0 &&
                                        confirmPassword !== newPassword
                                            ? 'Las contraseñas no coinciden'
                                            : ''
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setShowPassword((p) => !p)}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </div>
                        <div className="usuarios-modal-footer">
                            <Button
                                variant="outlined"
                                className="usuarios-modal-cancel"
                                onClick={() => setPhase(1)}
                            >
                                Volver
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                className="usuarios-modal-submit"
                                onClick={confirmNewPassword}
                                disabled={
                                    !newPassword ||
                                    !confirmPassword ||
                                    newPassword !== confirmPassword
                                }
                            >
                                Continuar
                            </Button>
                        </div>
                    </>
                );

            case 3:
                return (
                    <>
                        <div className="usuarios-modal-body usuarios-modal-body--confirm">
                            <p className="usuarios-confirm-text">
                                ¿Estás seguro de que deseas cambiar la contraseña de{' '}
                                <strong>{theUser?.usuario}</strong>?
                            </p>
                        </div>
                        <div className="usuarios-modal-footer">
                            <Button
                                variant="outlined"
                                className="usuarios-modal-cancel"
                                onClick={() => setPhase(2)}
                            >
                                Volver
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                className="usuarios-modal-submit"
                                onClick={savePassword}
                            >
                                Guardar contraseña
                            </Button>
                        </div>
                    </>
                );

            case 4:
                return (
                    <>
                        <div className="usuarios-modal-body usuarios-modal-body--success">
                            <CheckCircleOutlineIcon className="usuarios-success-icon" />
                            <p className="usuarios-success-title">¡Contraseña actualizada!</p>
                            <p className="usuarios-success-subtitle">
                                El usuario puede iniciar sesión con su nueva contraseña.
                            </p>
                        </div>
                        <div className="usuarios-modal-footer">
                            <Button
                                variant="contained"
                                color="primary"
                                className="usuarios-modal-submit"
                                onClick={handleClose}
                            >
                                Cerrar
                            </Button>
                        </div>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <div className="usuarios-modal usuarios-modal--wide">
                <div className="usuarios-modal-header">
                    <h2 className="usuarios-modal-title">{titles[phase]}</h2>
                    <IconButton size="small" onClick={handleClose}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>
                {renderBody()}
            </div>
        </Modal>
    );
}

ModalEditarUsuario.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
